import { ASSET_PREFIX, colors } from "utils/constants";
import { useEffect, useState } from "react";
import RoundedButton from "components/RoundedButton";
import { fetchPartnerDiscountCodeIfSubscriber, trackPartnerDiscountLinkClick } from "api/bonus";
import { getCookie } from "utils/cookie";
import PlainLoadingSpinner from "components/PlainLoadingSpinner";
import { copyToClipboard, getCta } from 'utils/common';
import Router from 'next/router';

const ModalState = {
  LOADING: 'LOADING',
  NO_ACCESS: 'NO_ACCESS',
  CODE_AVAILABLE: 'AVAILABLE'
}

const PartnerDiscountModal = ({ partnerDiscount, handleDismiss }) => {
  const { partnerCode, heroImage, iconComponent, discountComponent, conditionComponent } = partnerDiscount;

  const [modalState, setModalState] = useState(ModalState.LOADING);
  const [codeInfo, setCodeInfo] = useState(null);

  useEffect(() => {
    (async () => {
      const jwt = getCookie('jwt', null);

      if (jwt) {
        const result = await fetchPartnerDiscountCodeIfSubscriber(jwt, partnerCode);
        if (result.accessGranted) {
          setCodeInfo(result.codeInfo);
          setModalState(ModalState.CODE_AVAILABLE);
        } else {
          setModalState(ModalState.NO_ACCESS);
        }
      } else {
        setModalState(ModalState.NO_ACCESS);
      }
    })();
  }, []);

  return (
    <div
      className="container"
      id="dimmedBackground"
      onClick={(e) => e.target.id === 'dimmedBackground' && handleDismiss()}
    >
      <div className="message">
        <div className="heroSection">
          <img
            id="closeButton"
            src={`${ASSET_PREFIX}/images/close_button_white.svg`}
            style={{
              cursor: 'pointer',
              width: '15px',
              position: 'absolute',
              right: '1em',
              top: '1em',
            }}
            onClick={handleDismiss}
            alt="close button - cross"
          />

          {modalState === ModalState.NO_ACCESS && (
            <img
              src={`${ASSET_PREFIX}/images/subscriber-perks/padlock-white.svg`}
              style={{
                maxHeight: 80,
              }}
            />
          )}
        </div>

        <div
          className="paddedMaxWidthContainer"
          style={{ paddingTop: '1em' }}
        >
          <div
            className={`imageContainer${
              modalState === ModalState.NO_ACCESS ? ' dimmed' : ''
            }`}
          >
            {iconComponent}
          </div>

          <h5
            className={`${modalState === ModalState.NO_ACCESS ? 'dimmed' : ''}`}
            style={{ margin: '1em 0 0 0' }}
          >
            {discountComponent}
          </h5>

          <div
            className={`separator${
              modalState === ModalState.NO_ACCESS ? ' dimmed' : ''
            }`}
            style={{
              width: '80%',
              maxWidth: '300px',
              height: '3px',
              margin: '1em',
            }}
          />

          <p
            className={`conditionText${
              modalState === ModalState.NO_ACCESS ? ' dimmed' : ''
            }`}
            style={{
              fontSize: '1.25em',
              margin: '0 0 2em 0',
              width: '100%',
              maxWidth: '320px',
            }}
          >
            {conditionComponent}
          </p>

          {modalState === ModalState.LOADING ? (
            <PlainLoadingSpinner style={{ marginBottom: '2em' }} />
          ) : modalState === ModalState.CODE_AVAILABLE && codeInfo !== null ? (
            <>
              {codeInfo.code && (
                <>
                  <p style={{ fontWeight: 600, fontSize: '1.1em', margin: 0 }}>
                    Enter this code at checkout to claim discount
                  </p>

                  <div id="promoCode">{codeInfo.code}</div>
                </>
              )}

              <br />

              {codeInfo.link && (
                <RoundedButton
                  onClick={async () => {
                    if (codeInfo.code) {
                      copyToClipboard(codeInfo.code);
                    }
                    const jwt = getCookie('jwt', null);
                    await trackPartnerDiscountLinkClick(jwt, partnerCode);
                    await Router.push(codeInfo.link);
                  }}
                  className="doneButton"
                  style={{
                    marginTop: 0,
                  }}
                >
                  {codeInfo.code ? 'Copy Code & ' : ''}Launch Site
                </RoundedButton>
              )}
            </>
          ) : (
            <>
              <p style={{ fontWeight: 600, fontSize: '1.1em', margin: 0 }}>
                Subscribe to unlock this discount
              </p>

              <RoundedButton
                href={getCta()?.url}
                className="doneButton"
              >
                Subscribe Now
              </RoundedButton>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          max-width: 100%;
          height: 100%;
          background-color: rgba(55, 55, 55, 0.5);
          z-index: 999;
        }

        .message {
          position: fixed;
          top: 50%;
          left: 50%;
          width: 95%;
          max-width: 600px;
          max-height: 95%;
          overflow-y: auto;
          -webkit-transform: translate(-50%, -50%);
          transform: translate(-50%, -50%);

          display: flex;
          flex-direction: column;
          align-items: center;
          border-radius: 5px;
          box-shadow: 0 0 12px 12px rgba(0, 0, 0, 0.25);
          background-color: white;
          z-index: 1000;
        }

        .heroSection {
          background-image: url(${heroImage});
          background-size: cover;
          background-position: center;
          width: 100%;
          min-height: 110px;
          flex-shrink: 0;
          box-shadow: inset 0 0 0 1000px
            rgba(
              0,
              0,
              0,
              ${modalState === ModalState.NO_ACCESS ? '0.6' : '0.3'}
            );
          padding: 2em;
          display: flex;
          flex-direction: column;
        }

        .imageContainer {
          height: 100px;
          width: 140px;
          margin: 0 auto 1em auto;
          text-align: center;
          display: flex;
          justify-content: center;
          align-items: center;
          flex-shrink: 0;
        }

        .dimmed {
          opacity: 0.3;
        }

        :global(.message .imageContainer img) {
          height: auto;
          max-height: 100%;
          width: auto;
          max-width: 100%;
        }

        .conditionText {
          font-style: italic;
          font-weight: 600;
          text-align: center;
        }

        #promoCode {
          font-size: 16px;
          color: ${colors.primaryBrandGray};
          border: 2px solid #9b9b9b;
          border-radius: 5px;
          padding: 0.4em 1.6em;
          margin: 14px auto 0 auto;
          white-space: nowrap;
          font-weight: bold;
        }

        :global(.doneButton) {
          position: relative;
          min-width: 210px;
          justify-self: center;
          margin: 20px;
          text-transform: uppercase;
        }

        // This is needed to add padding below the button when scrolling is enabled on the parent div.
        :global(.doneButton:after) {
          content: '';
          display: block;
          position: absolute;
          bottom: -2rem;
          width: 1px;
          height: 2rem;
        }
      `}</style>
    </div>
  );
};

export default PartnerDiscountModal;
